import { useEffect } from 'react';
import PropTypes from 'prop-types';

const EnvironmentIndicator = ({ backendSpecsActions, backendSpecsSelectors, userRole, userEnvironmentResolved }) => {
  const activeEnvironment = backendSpecsSelectors?.selectActiveEnvironment?.() || null;
  const environments = backendSpecsSelectors?.selectEnvironments?.() || [];
  const isLoading = backendSpecsSelectors?.selectEnvironmentsLoading?.() || false;

  const isViewerEnv = userRole === 'MOCKORCHESTRATOR_VIEWER_ENV';
  const isViewer = userRole === 'MOCKORCHESTRATOR_VIEWER';

  useEffect(() => {
    if (backendSpecsActions?.fetchEnvironments) {
      backendSpecsActions.fetchEnvironments();
    }
  }, [backendSpecsActions]);

  // VIEWER_ENV: auto-set personal environment on mount
  useEffect(() => {
    if (isViewerEnv && userEnvironmentResolved && backendSpecsActions?.setActiveEnvironment) {
      backendSpecsActions.setActiveEnvironment({
        id: userEnvironmentResolved.id,
        name: userEnvironmentResolved.name,
      });
    }
  }, [isViewerEnv, userEnvironmentResolved, backendSpecsActions]);

  // Role not yet resolved — render nothing
  if (!userRole) {
    return null;
  }

  // VIEWER: hide the environment selector entirely
  if (isViewer) {
    return null;
  }

  // VIEWER_ENV: show only the personal environment, locked (no "Default" option)
  if (isViewerEnv) {
    if (!userEnvironmentResolved) return null;
    return (
      <div className="environment-indicator">
        <label className="environment-indicator__label">
          <span className="environment-indicator__prefix">Env:</span>
          <select
            className="environment-indicator__select"
            value={String(userEnvironmentResolved.id)}
            disabled
          >
            <option value={String(userEnvironmentResolved.id)}>{userEnvironmentResolved.name}</option>
          </select>
        </label>
      </div>
    );
  }

  const handleChange = (e) => {
    const value = e.target.value;
    if (value === '') {
      backendSpecsActions?.setActiveEnvironment?.(null);
    } else {
      const env = environments.find((env) => String(env.id) === value);
      if (env) {
        backendSpecsActions?.setActiveEnvironment?.({ id: env.id, name: env.name });
      }
    }
  };

  if (!backendSpecsSelectors || environments.length === 0) {
    return null;
  }

  return (
    <div className="environment-indicator">
      <label className="environment-indicator__label">
        <span className="environment-indicator__prefix">Env:</span>
        <select
          className="environment-indicator__select"
          value={activeEnvironment ? String(activeEnvironment.id) : ''}
          onChange={handleChange}
          disabled={isLoading}
        >
          <option value="">Default</option>
          {environments.map((env) => (
            <option key={env.id} value={String(env.id)}>
              {env.name}
            </option>
          ))}
        </select>
      </label>
    </div>
  );
};

EnvironmentIndicator.propTypes = {
  backendSpecsActions: PropTypes.object,
  backendSpecsSelectors: PropTypes.object,
  userRole: PropTypes.string,
  userEnvironmentResolved: PropTypes.object,
};

EnvironmentIndicator.defaultProps = {
  backendSpecsActions: null,
  backendSpecsSelectors: null,
  userRole: null,
  userEnvironmentResolved: null,
};

export default EnvironmentIndicator;
